/**
 * ================================================================================================
 *  WHERE AN AUTHENTICATED PRINCIPAL LANDS — ONE RULE, READ FROM THE SERVER
 * ================================================================================================
 *
 * A Platform Owner completed password and MFA and was sent to `/companies`, because three separate
 * places each named that address as "after sign-in":
 *
 *     App.tsx            <Route path="/" element={<Navigate to={routes.companies} />} />
 *     App.tsx            onSignedIn={() => navigate(routes.companies)}
 *     MfaChallengeScreen window.location.assign(routes.companies)
 *
 * None of them was wrong about tenants. All of them were wrong about the other surface, and there
 * was no single place where that could be noticed — which is what makes this a defect about
 * STRUCTURE rather than about three literals. A platform account has no companies at all, so the
 * landing was a screen whose only honest content is a refusal.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────────────────────────
 *
 * The surface comes from the SERVER, from `auth.whoami.read`, and from nothing else. Not from the
 * address the browser happens to be at, not from the login that was typed, not from anything held
 * in local state, and not from the route that was requested before sign-in. `app_user.account_kind`
 * decides the context at authentication and cannot change (OA-0001 §2.1), so the server's answer is
 * the only source that could be right — and CM-014 is the record of what happens when the browser
 * is allowed to have an opinion: typing `/platform` granted the platform surface.
 *
 * ── WHY THIS IS A MODULE AND NOT A CONSTANT ─────────────────────────────────────────────────────
 *
 * Because the three call sites navigate by three different MECHANISMS, deliberately: a client route
 * after sign-in, a full page load after MFA (the session cookie has just changed and starting the
 * app again is the simplest way to be sure nothing stale survives), and a redirect at the root. The
 * mechanism is each call site's business. The DESTINATION is not, and it lives here.
 */

import type { SessionContext } from '../api/contract';
import { callOperation, sessionFromWhoami } from '../api/operations';
import { routes } from '../routes';

/**
 * The home address of a surface.
 *
 * Exhaustive over `SessionContext` by construction — a `Record`, not a chain of `if`s — so adding a
 * third context would be a type error here rather than a silent fall-through to the tenant
 * workspace, which is precisely how a platform account ended up at `/companies`.
 */
const HOME: Readonly<Record<SessionContext, string>> = {
  PLATFORM: routes.platformConsole,
  /*
   * THE TENANT LANDS ON THEIR OWN HOME, not on a picker.
   *
   * The approved customer kit opens on הבית שלי and lists החברות שלי as a separate destination.
   * This product had only the second, so signing in put a person in front of a list of companies
   * before anything told them where they were. `routes.companies` keeps its address and its
   * screen; it is simply no longer the first thing an office sees.
   */
  TENANT: routes.tenantHome,
};

/**
 * HOW EACH FRONT DOOR NAMES ITSELF.
 *
 * The sign-in surface is the one screen rendered before any session exists, so it cannot take its
 * identity from `/api/me` the way every screen after it does. It asks `portal.identity.read`
 * instead — the SERVER answering which host the request arrived at — rather than reading
 * `window.location`, because a browser deciding which surface it is on is CM-014's shape even when
 * the consequence is only a heading.
 *
 * THIS DECIDES NOTHING. The portal that matters is re-derived server-side on every sign-in and on
 * every authenticated request. If this text were wrong, a platform account would still be refused
 * at the customer host and a tenant account at the console; the screen would simply be mislabelled.
 */
export interface PortalPresentation {
  readonly titleHe: string;
  readonly leadHe: string;
}

export const PORTAL_PRESENTATION: Readonly<Record<SessionContext, PortalPresentation>> = {
  TENANT: {
    titleHe: 'GL-POINT',
    leadHe: 'התחברות לסביבת הארגון',
  },
  PLATFORM: {
    titleHe: 'GL-POINT · ניהול הפלטפורמה',
    leadHe: 'התחברות לקונסולת הפלטפורמה',
  },
};

/** Where a session of this context belongs. The one place that answers this question. */
export function landingFor(context: SessionContext): string {
  return HOME[context];
}

/**
 * Ask the server what this session is, and return where it belongs.
 *
 * REJECTS rather than guessing. A whoami that did not answer is not a tenant session — it is an
 * unknown one, and `onUnauthenticated` already routes a 401 to sign-in. A fallback here would be a
 * second decision about the same question, and the two would disagree exactly on the surface that
 * matters: a platform principal whose whoami was slow would be sent into the tenant workspace.
 */
export async function resolveLandingPath(): Promise<string> {
  const me = await callOperation('auth.whoami.read', {});

  /*
   * NARROWED BY `sessionFromWhoami`, NOT HERE. The wire carries `context` as a string, and there is
   * already exactly one place that decides which strings are surfaces -- it refuses anything else
   * rather than defaulting, because defaulting an unrecognised context to TENANT would GRANT a
   * surface. A second narrowing in this file would be a second answer to that question, and the
   * two would differ on precisely the input nobody thought of.
   */
  const session = sessionFromWhoami(me);
  if (session === null) {
    throw new Error('the server did not state a surface for this session');
  }
  return landingFor(session.sessionContext);
}
